import axios from 'axios';
import Cookies from 'js-cookie';
import moment from 'moment';
import { extendMoment } from 'moment-range';
import { dineAroundBreakfastType } from './dineAround/dineAroundConstants';

const getHeaders = (bookingSubscriptionKey, isConfig) => {
  const sessionId = Cookies.get('_bmeSessionId');
  const headers = { 'Content-Type': 'application/json' };
  if (bookingSubscriptionKey) headers['Ocp-Apim-Subscription-Key'] = bookingSubscriptionKey;
  if (sessionId && !isConfig) headers['x-bl-rds'] = sessionId;
  return headers;
};

export const callApi = (options, bookingPrefix, bookingSubscriptionKey, timeout = 60000, isConfig) => axios({
  ...options,
  baseURL: bookingPrefix,
  headers: getHeaders(bookingSubscriptionKey, isConfig),
  timeout: parseInt(timeout, 10),
});

export const getLoginMessage = () => 'Please login';
export const getNotFullyPaidMessage = () => 'You have to fully pay your break to sign in.';
export const getNothingToBookMessage = () => 'We know how excited you are to get planning, our booking system isn’t open just yet for your break. Please check back in a couple of days or look out for an email from us letting you know when you can book.'; // eslint-disable-line
export const getTimeSlotIsFullMessage = () => 'Sorry but this slot is full.\nTry booking an other slot or try this one later.';
export const getOnlyAllowStatusMessage = (statusCode = '') => {
  if (statusCode === 'CX') return 'Your booking is been cancelled';
  return 'Your booking is not confirm, please contact us to discuss';
};
export const getNotFoundBookingMessage = () => 'Sorry your booking could not be found';
export const getNotAllowedForSunBookersMessage = () => `To access and book shows and swimming sessions, you will need to add an entertainment pass to your booking. 
If you’d like to add an entertainment pass please email our team at`;
export const getBreakIsFinishedMessage = () => 'This booking has now finished';

const get400ErrorMessage = (errorMessage, { url = '' }) => {
  if (url.includes('booking/search')) return getNotFoundBookingMessage();
  if (errorMessage.includes('session')) return getLoginMessage();
  return 'Something went wrong :(';
};

const getTimeoutMessage = isLogin => (isLogin
  ? 'There has been an issue logging you in. Please check your internet connection and try again'
  : 'There has been an issue retrieving your show / pool bookings. Please check your internet connection and try again');

const get500ErrorMessage = (errorMessage, isLogin) => {
  if (errorMessage === 'insufficient capacity remaining for activity') return getTimeSlotIsFullMessage();
  if (errorMessage === 'the operation has timed out') return getTimeoutMessage(isLogin);
  return 'Something went wrong :(';
};

export const getErrorMessage = (error, isLogin) => {
  const { response = {}, request, config = {}, code, message } = error;
  if (response && Object.keys(response).length > 0) {
    const { data = {}, status } = response;
    const [errorFromApi = {}] = data.Errors || [];
    const { Message = '' } = errorFromApi;
    const errorMessage = Message.toLowerCase();

    if (status === 401) return getLoginMessage();
    if (status === 400) return get400ErrorMessage(errorMessage, config);
    if (status === 500) return get500ErrorMessage(errorMessage, isLogin);
    return 'Something went wrong :(';
  }
  if (code === 'ECONNABORTED' && message.includes('timeout')) return getTimeoutMessage(isLogin);
  if (request) return 'Not responding :(';
  return 'Sorry we can not execute this action at the moment';
};

export const getDayLabel = (currentDateString) => {
  const now = moment();
  const today = now.date();
  const currentDate = moment(currentDateString);
  const dayDiff = currentDate.date() - today;
  const monthDiff = currentDate.month() - now.month();
  const yearDiff = currentDate.year() - now.year();

  if (now.isSame(currentDate, 'day')) return `TODAY · ${currentDate.format('Do MMMM')}`;
  if (yearDiff === 0 && monthDiff === 0 && dayDiff === 1) return `TOMORROW · ${currentDate.format('Do MMMM')}`;
  const dayOfWeek = currentDate.format('dddd');
  return `${dayOfWeek.toUpperCase()} · ${currentDate.format('Do MMMM')}`;
};

export const breakfastType = 'BME BREAKFAST';
export const dinnerType = 'BME DINNER';
export const entertainmentType = 'BME ENTERTAINMENT';
export const arrivalType = 'BME ARRIVAL';

export const getAllCookies = () => ({
  siteCode: Cookies.get('_siteCode'),
  resId: parseInt(Cookies.get('_resId'), 10),
  lastName: Cookies.get('_lastName'),
  arrivalDate: Cookies.get('_arrivalDate'),
  departureDate: Cookies.get('_departureDate'),
  numOfGuests: parseInt(Cookies.get('_numOfGuests'), 10),
  mainGuestFirstName: Cookies.get('_mainGuestFirstName'),
  numOfAdults: parseInt(Cookies.get('_numOfAdults'), 10),
  numOfChildren: parseInt(Cookies.get('_numOfChildren'), 10),
  numOfToddlers: parseInt(Cookies.get('_numOfToddlers'), 10),
  numOfInfants: parseInt(Cookies.get('_numOfInfants'), 10),
  numOfUnits: parseInt(Cookies.get('_numOfUnits'), 10),
  numOfUnderFifteens: parseInt(Cookies.get('_numOfUnderFifteens'), 10),
  fullName: Cookies.get('_fullName'),
  skipOnBoarding: Cookies.get('_skipOnBoarding') === 'true',
  isDaBooking: Cookies.get('_isDaBooking') === 'true',
  secretCode: Cookies.get('_secretCode'),
  isEarlyAccess: Cookies.get('_isEarlyAccess') === 'true',
  agencyType: Cookies.get('_agencyType'),
  seasonType: Cookies.get('_seasonType'),
  isHbs: Cookies.get('_isHbs') === 'true',
});

export const removeCookies = () => {
  Cookies.remove('_siteCode');
  Cookies.remove('_resId');
  Cookies.remove('_lastName');
  Cookies.remove('_arrivalDate');
  Cookies.remove('_departureDate');
  Cookies.remove('_numOfGuests');
  Cookies.remove('_mainGuestFirstName');
  Cookies.remove('_numOfAdults');
  Cookies.remove('_numOfChildren');
  Cookies.remove('_numOfToddlers');
  Cookies.remove('_numOfInfants');
  Cookies.remove('_numOfUnits');
  Cookies.remove('_numOfUnderFifteens');
  Cookies.remove('_fullName');
  Cookies.remove('_isDaBooking');
  Cookies.remove('_secretCode');
  Cookies.remove('_isEarlyAccess');
  Cookies.remove('_agencyType');
  Cookies.remove('_seasonType');
  Cookies.remove('_isHbs');
};

export const setCookies = ({
  siteCode,
  resId,
  lastName,
  formattedArrivalDate,
  departureDate,
  numOfGuests,
  mainGuestFirstName,
  numOfAdults,
  numOfChildren,
  numOfToddlers,
  numOfInfants,
  numOfUnits,
  numOfUnderFifteens,
  fullName,
  isDaBooking,
  secretCode,
  isEarlyAccess,
  agencyType,
  seasonType,
  isHbs,
}) => {
  Cookies.set('_siteCode', siteCode, { expires: 7 });
  Cookies.set('_resId', resId, { expires: 7 });
  Cookies.set('_lastName', lastName, { expires: 7 });
  Cookies.set('_arrivalDate', formattedArrivalDate, { expires: 7 });
  Cookies.set('_departureDate', departureDate, { expires: 7 });
  Cookies.set('_numOfGuests', numOfGuests, { expires: 7 });
  Cookies.set('_mainGuestFirstName', mainGuestFirstName, { expires: 7 });
  Cookies.set('_numOfAdults', numOfAdults, { expires: 7 });
  Cookies.set('_numOfChildren', numOfChildren, { expires: 7 });
  Cookies.set('_numOfToddlers', numOfToddlers, { expires: 7 });
  Cookies.set('_numOfInfants', numOfInfants, { expires: 7 });
  Cookies.set('_numOfUnits', numOfUnits, { expires: 7 });
  Cookies.set('_numOfUnderFifteens', numOfUnderFifteens, { expires: 7 });
  Cookies.set('_fullName', fullName, { expires: 7 });
  Cookies.set('_isDaBooking', isDaBooking, { expires: 7 });
  Cookies.set('_secretCode', secretCode, { expires: 7 });
  Cookies.set('_isEarlyAccess', isEarlyAccess, { expires: 7 });
  Cookies.set('_agencyType', agencyType, { expires: 7 });
  Cookies.set('_seasonType', seasonType, { expires: 7 });
  Cookies.set('_isHbs', isHbs, { expires: 7 });
};

const getGuestType = ({ age }) => {
  if (age === -1) return 'adult';
  if (age < 2) return 'infant';
  if (age < 6) return 'toddler';
  if (age < 15) return 'child';
  return 'adult';
};

export const getNumOfGuestsByType = (units = []) => {
  const allGuests = units.map(({ Booking = {} }) => {
    const { guests: guestsOfThisUnit = [], status = {} } = Booking;
    const { code = '' } = status;
    if (code !== 'BK') return [];
    return guestsOfThisUnit;
  });
  const numOfGuests = units.reduce((currentGuestCount, { Booking = {} }) => {
    const { guestCount = 1, status = {} } = Booking;
    const { code = '' } = status;
    if (code !== 'BK') return currentGuestCount;
    return guestCount + currentGuestCount;
  }, 0);
  const guests = allGuests.flat();
  const guestTypes = guests.map(getGuestType);
  return {
    numOfGuests,
    numOfAdults: guestTypes.filter(type => type === 'adult').length,
    numOfChildren: guestTypes.filter(type => type === 'child').length,
    numOfToddlers: guestTypes.filter(type => type === 'toddler').length,
    numOfInfants: guestTypes.filter(type => type === 'infant').length,
    numOfUnderFifteens: guests.filter(({ age }) => age < 15 && age !== -1).length,
  };
};

export const getAlreadyBookedDinings = ({ addons = [] }) => {
  const [bookedBreakfast = {}] = addons
    .filter(({ AddonCategory, QuantityBooked }) => AddonCategory === breakfastType && QuantityBooked > 0);

  const [bookedDinner = {}] = addons
    .filter(({ AddonCategory, QuantityBooked }) => AddonCategory === dinnerType && QuantityBooked > 0);

  return { bookedBreakfast, bookedDinner };
};

export const loginPage = '?page=login';
export const breakfastPage = '?page=breakfast';
export const dinnerPage = '?page=dinner';
export const entertainmentPage = '?page=entertainment';
export const entertainmentDetailsPage = '?page=entertainment-detail';
export const swimmingPage = '?page=swimming';
export const swimmingDetailsPage = '?page=swimming-detail';
export const schedulePage = '?page=schedule';
export const eventPage = '?page=event';
export const summaryPage = '?page=summary';
export const testPage = '?page=test';
export const arrivalTimePage = '?page=arrival-time';
export const dineAroundPage = '?page=dine-around';
export const dineAroundDetailsPage = '?page=dine-around-detail';

const formatTime = (timeString) => {
  if (!timeString) return '';
  if (timeString.includes('am')) {
    const [time] = timeString.split('am');
    const timeWhitoutWhiteSpaces = time.replace(/ /g, '');
    const [hourString, minuteString] = timeWhitoutWhiteSpaces.split(':');
    return !minuteString ? `${hourString}:00` : `${hourString}:${minuteString}`;
  }
  if (timeString.includes('pm')) {
    const [time] = timeString.split('pm');
    const timeWhitoutWhiteSpaces = time.replace(/ /g, '');
    const [hourString, minuteString] = timeWhitoutWhiteSpaces.split(':');
    const hour = parseInt(hourString, 10) + 12;
    return !minuteString ? `${hour}:00` : `${hour}:${minuteString}`;
  }

  const [hourString, minuteString] = timeString.split(':');
  return !minuteString ? `${hourString}:00` : `${hourString}:${minuteString}`;
};

const getTimeWithSuffix = (chunksInLowerCase, timeString, isEnd) => {
  if (timeString === 'am' || timeString === 'pm') {
    const timeStringIndex = !isEnd ? chunksInLowerCase.indexOf(timeString) : chunksInLowerCase.lastIndexOf(timeString);
    const timeNumber = chunksInLowerCase[timeStringIndex - 1];
    return Number.isNaN(parseInt(timeNumber, 10)) ? '' : `${timeNumber}${timeString}`;
  }
  return Number.isNaN(parseInt(timeString, 10)) ? '' : timeString;
};

const getTimeChunks = (chunksInLowerCase) => {
  const chunks = chunksInLowerCase.filter(chunk => chunk.includes('am') || chunk.includes('pm'));
  if (chunks.length === 2) return chunks;
  return chunksInLowerCase.filter(chunk => !Number.isNaN(parseInt(chunk, 10)));
};

const getTimeStrings = (chunksInLowerCase = []) => {
  const [startTime, endTime] = getTimeChunks(chunksInLowerCase);
  const startTimeString = getTimeWithSuffix(chunksInLowerCase, startTime);
  const endTimeString = getTimeWithSuffix(chunksInLowerCase, endTime, true);
  return { startTimeString, endTimeString };
};

export const getTimes = (comment) => {
  const commentChunks = comment.split(' ');
  const chunksInLowerCase = commentChunks.map(chunk => chunk.toLowerCase());
  const { startTimeString, endTimeString } = getTimeStrings(chunksInLowerCase);
  const startTime = formatTime(startTimeString);
  const endTime = formatTime(endTimeString);
  return { startTime, endTime };
};

export const compareEventsByStartTime = ({ startTime: startTimeA = '' }, { startTime: startTimeB = '' }) => {
  const [hourAstring, minuteAstring = '0'] = startTimeA.split(':');
  const [hourBstring, minuteBstring = '0'] = startTimeB.split(':');
  const hourA = parseInt(hourAstring.trim(), 10);
  const hourB = parseInt(hourBstring.trim(), 10);
  const minuteA = parseInt(minuteAstring.trim(), 10);
  const minuteB = parseInt(minuteBstring.trim(), 10);
  if (hourA < hourB) return -1;
  if (hourA > hourB) return 1;
  if (minuteA < minuteB) return -1;
  if (minuteA > minuteB) return 1;
  return 0;
};

export const shouldFetch = (
  { isFetching, wasCalled },
  { isBookingDataSetFromCookies },
  { REACT_APP_BOOKING_PREFIX, REACT_APP_BOOKING_SUBSCRIPTION_KEY },
) => isBookingDataSetFromCookies
&& !isFetching
&& !wasCalled
&& !!REACT_APP_BOOKING_PREFIX
  && !!REACT_APP_BOOKING_SUBSCRIPTION_KEY;

export const shouldFetchDining = (dinings, bookingSearch, config) => {
  const { siteCode } = bookingSearch;
  return shouldFetch(dinings, bookingSearch, config) && config[`REACT_APP_SHOW_DINING_${siteCode.toUpperCase()}`] === 'true';
};

export const getHoursAndMinutes = (timeString = '13:00') => {
  const [hourString = '13', minuteString = '0'] = timeString.split(':');
  return {
    hours: parseInt(hourString, 10),
    minutes: parseInt(minuteString, 10),
  };
};

const getDepartureTime = (useCustomTime, latestBookingDateEndTime, departureTimeFromConfig) => {
  const departureTime = useCustomTime ? latestBookingDateEndTime : departureTimeFromConfig || '22:00';
  const [departureHourString = '22', departureMinuteString = '0'] = departureTime.split(':');
  return {
    departureHour: parseInt(departureHourString, 10),
    departureMinute: parseInt(departureMinuteString, 10),
  };
};

export const filterSlotsByArrivalAndDepartureTime = (
  { Starts },
  arrivalDateString,
  departureDateString,
  useCustomTime,
  arrivalTime,
  latestBookingDateEndTime,
  departureTime,
) => {
  const startDate = moment(Starts);
  const arrivalDate = moment(arrivalDateString);
  const departureDate = moment(departureDateString);
  const isArrivalDate = startDate.isSame(arrivalDate, 'day');
  const isDepartureDate = startDate.isSame(departureDate, 'day');
  if (!isArrivalDate && !isDepartureDate) return true;
  const startHour = startDate.hour();
  const startMinute = startDate.minute();
  if (isArrivalDate) {
    const { hours: arrivalHour, minutes: arrivalMinute } = getHoursAndMinutes(arrivalTime);
    if (startHour < arrivalHour) return false;
    if (startHour === arrivalHour && startMinute < arrivalMinute) return false;
    return true;
  }
  if (isDepartureDate) {
    const { departureHour, departureMinute } = getDepartureTime(useCustomTime, latestBookingDateEndTime, departureTime);
    if (startHour > departureHour) return false;
    if (startHour === departureHour && startMinute > departureMinute) return false;
    return true;
  }
  return true;
};

export const isLatestBookableDateBeforeDepartureDate = (departureDate, latestActivityBookingDate) => {
  if (!latestActivityBookingDate) return departureDate;
  const momentLatestActivityBookingDate = moment(latestActivityBookingDate);
  const momentDepartureDate = moment(departureDate);
  return !(momentDepartureDate.diff(momentLatestActivityBookingDate, 'day') < 0);
};

export const filterPastEvents = ({ startDate: startDateString, endTime: endTimeString }) => {
  const today = moment();
  const startDate = moment(startDateString);
  if (startDate.isBefore(today, 'day')) return false;
  if (startDate.isSame(today, 'day')) {
    const currentHour = moment().hour();
    const currentMinute = moment().minutes();
    const endTime = endTimeString.split(':');
    const endHour = parseInt(endTime[0], 10);
    const endMinute = parseInt(endTime[1], 10);
    if (endHour < currentHour) return false;
    if ((endHour === currentHour) && endMinute < currentMinute) return false;
  }
  return true;
};

export const getTimeSlotIndexById = (availableEntertainments, timeSlotId, arrivalTime, latestBookingDateEndTime, departureTime, latestActivityBookingDate) => {
  if (!availableEntertainments || !timeSlotId) return -1;
  const slotId = parseInt(timeSlotId, 10);
  let slotIndex = -1;

  const arrivalDate = Cookies.get('_arrivalDate');
  const departureDate = Cookies.get('_departureDate');
  const useCustomTime = isLatestBookableDateBeforeDepartureDate(departureDate, latestActivityBookingDate);

  availableEntertainments.forEach(({ Locations = [], Duration: duration }) => {
    Locations.forEach(({ TimeSlots = [] }) => {
      if (TimeSlots && TimeSlots.length > 0) {
        const filteredTimeSlots = TimeSlots
          .filter(slot => filterSlotsByArrivalAndDepartureTime(
            slot,
            arrivalDate,
            departureDate,
            useCustomTime,
            arrivalTime,
            latestBookingDateEndTime,
            departureTime,
          ))
          .filter(({ Starts }) => filterPastEvents({ startDate: Starts, endTime: moment(Starts).add(duration, 'minute').format('HH:mm') }));
        filteredTimeSlots.sort((slotA, slotB) => moment(slotA.Starts).diff(moment(slotB.Starts), 'minutes'));
        const index = filteredTimeSlots.findIndex(({ Id }) => Id === slotId);
        if (index !== -1) slotIndex = index;
      }
    });
  });

  return slotIndex;
};

const getHasBookedDining = (bookedBreakfast, bookedDinner, diningType) => (diningType === breakfastType
  ? !!bookedBreakfast.AddonCategory
  : !!bookedDinner.AddonCategory);

export const getIsAllowedToChangeDining = ({ bookedBreakfast, bookedDinner }, { arrivalDate: arrivalDateString }, diningType) => {
  const arrivalDate = moment(arrivalDateString);
  const hasBookedDining = getHasBookedDining(bookedBreakfast, bookedDinner, diningType);
  return arrivalDate.isAfter(moment()) || !hasBookedDining;
};

export const getShouldSkipDiningPage = (dinings, bookingSearch, diningType, config) => {
  const { isDaBooking, siteCode } = bookingSearch;
  const showDiningPage = config[`REACT_APP_SHOW_DINING_${siteCode.toUpperCase()}`] === 'true';
  if (isDaBooking || !showDiningPage) return true;
  const { wasCalled, rawDinings = [], diningSlots = [] } = dinings;
  const isResponseUseful = rawDinings.find(({ AddonCategory }) => AddonCategory === diningType);
  const containsValidSlots = diningSlots.find(({ type }) => type === diningType);
  const isAllowedToChangeDining = getIsAllowedToChangeDining(dinings, bookingSearch, diningType);
  return wasCalled && (!isResponseUseful || !containsValidSlots || !isAllowedToChangeDining);
};

export const getDiningColor = (code = '') => {
  const codeChunks = code.split(' ');
  const lastChunk = codeChunks[codeChunks.length - 1];
  return lastChunk === 'E' ? 'slot-color-1' : 'slot-color-2';
};

export const getShouldFetchAvailableEntertainments = (availableEntertainments, bookedEntertainments, bookingSearch, config) => {
  const shouldFetchIt = shouldFetch(availableEntertainments, bookingSearch, config);
  return shouldFetchIt && bookedEntertainments.wasCalled;
};

export const checkAddonsByCode = (addons = [], type = '') => {
  let checkAddon = false;
  if (!addons || addons.length === 0 || type === '') {
    return checkAddon;
  }
  addons.forEach(({ addon }) => {
    const { code } = addon;
    if (code === type) {
      checkAddon = true;
    }
  });
  return checkAddon;
};

export const filterBookedEntertainments = (availableEntertainments, bookedEntertainments) => {
  const newBookedEntertainments = [];
  if (!availableEntertainments || availableEntertainments.length === 0 || !bookedEntertainments || bookedEntertainments.length === 0) {
    return newBookedEntertainments;
  }
  bookedEntertainments.forEach((bookedEntertainment) => {
    const { Time: time, TimeSlotId: timeSlotId } = bookedEntertainment;
    availableEntertainments.forEach(({ date, events }) => {
      const momentDate = moment(date);
      const bookedMomentTime = moment(time);
      if (momentDate.diff(bookedMomentTime, 'day') === 0) {
        events.forEach(({ slots }) => {
          slots.forEach(({ Id: availableTimeSlotId }) => {
            if (availableTimeSlotId === timeSlotId) {
              newBookedEntertainments.push(bookedEntertainment);
            }
          });
        });
      }
    });
  });
  return newBookedEntertainments;
};

export const getCapitalizedName = (name = '') => {
  const nameInLowerCase = name.toLowerCase();
  if (nameInLowerCase.includes(' ')) {
    const nameChunks = nameInLowerCase.split(' ');
    const capitalizedNames = nameChunks.map(getCapitalizedName);
    return capitalizedNames.join(' ');
  }
  return `${nameInLowerCase.charAt(0).toUpperCase()}${nameInLowerCase.slice(1)}`;
};

export const isSplash = (name = '') => name.toLowerCase().includes('splash');

export const getQuantity = (entertainmentName = '', numOfUnits, numOfGuests, isPerPersonActivity) => {
  const isPool = isSplash(entertainmentName);
  return isPool || isPerPersonActivity ? numOfGuests : numOfUnits;
};

export const isNowBetweenLateBookingTimeRange = (singleDate, duration, lateBookingWindowMinutes) => {
  const allowedDate = moment(singleDate).format('YYYY-MM-DD');
  const allowedStartTime = moment(singleDate).subtract(lateBookingWindowMinutes, 'minute').format('HH:mm');
  const allowedEndTime = moment(singleDate).add(duration, 'minute').format('HH:mm');
  return moment().isBetween(`${allowedDate} ${allowedStartTime}`, `${allowedDate} ${allowedEndTime}`);
};

export const getOnboardingSkip = () => {
  const skipOnBoarding = Cookies.get('_skipOnBoarding');
  return skipOnBoarding === 'true';
};

export const setOnboardingSkip = () => {
  Cookies.set('_skipOnBoarding', 'true', { expires: 7 });
};

export const getSiteCode = () => Cookies.get('_siteCode');

export const getUserLoggedIn = () => {
  const resId = Cookies.get('_resId');
  if (!resId) return false;
  const resIdNum = parseInt(resId, 10);
  if (!resIdNum) return false;
  if (!Number.isInteger(resIdNum)) return false;
  return true;
};

export const isCurrentTimeAfterEvent = (date = '', duration = 0) => {
  if (date === '' || duration === 0) {
    return false;
  }
  const today = moment();
  if (!today.isSame(moment(date), 'day')) {
    return false;
  }
  const currentHour = moment().hour();
  const currentMinute = moment().minutes();
  const eventDateHours = moment(date).add(duration, 'minute').hour();
  const eventDateMinutes = moment(date).add(duration, 'minute').minute();
  if (currentHour > eventDateHours) return true;
  if (currentHour === eventDateHours && currentMinute >= eventDateMinutes) return true;
  return false;
};

export const getDateLabel = ({ singleDate, duration, startTime, endTime }) => {
  if (startTime && endTime) return `${startTime} - ${endTime}`;
  const eventStartTime = moment(singleDate);
  const eventEndTime = moment(eventStartTime).add(duration, 'minutes');
  return `${eventStartTime.format('HH:mm')} - ${eventEndTime.format('HH:mm')}`;
};

export const isNowAfterMoment = (timeString, now = moment()) => {
  const currentHour = now.hour();
  const currentMinute = now.minutes();
  const { hours, minutes } = getHoursAndMinutes(timeString);
  return currentHour > hours || (currentHour === hours && currentMinute > minutes);
};

export const isNowAfterTime = (dateString, timeString, now = moment()) => {
  const date = moment(dateString);
  if (now.isAfter(date, 'day')) return true;
  if (now.isSame(date, 'day')) return isNowAfterMoment(timeString, now);
  return false;
};

const capitalizeFirstLetter = myString => myString.trim().replace(/^\w/, character => character.toUpperCase());

export const getFullNameForSaveEntertainment = () => {
  const fullName = Cookies.get('_fullName');
  if (!fullName) {
    return '';
  }
  if (!fullName.includes(' ')) {
    return fullName;
  }
  const [firstName, lastName] = fullName.split(' ');
  return `${lastName.toUpperCase()}, ${capitalizeFirstLetter(firstName.toLocaleLowerCase())} [PMD]`;
};

export const getResortNameByType = (siteCode = '') => {
  if (siteCode === '') return '';
  switch (siteCode) {
    case 'BG':
      return 'bognor regis';
    case 'MH':
      return 'minehead';
    case 'SK':
      return 'skegness';
    default:
      return '';
  }
};

export const getLatestEntertainmentBookableDate = (departureDate, latestActivityBookingDate) => {
  if (!latestActivityBookingDate) {
    return departureDate;
  }
  const momentLatestActivityBookingDate = moment(latestActivityBookingDate);
  const momentDepartureDate = moment(departureDate);
  return momentDepartureDate.diff(momentLatestActivityBookingDate, 'day') < 0 ? departureDate : latestActivityBookingDate;
};

export const isEntertainmentButNotSplash = (name = '') => {
  if (name === '') return false;
  return !isSplash(name) && !name.toLowerCase().includes('breakfast') && !name.toLowerCase().includes('dinner');
};

export const getIsArrivalTimeEditable = (
  config,
  bookingSearch,
  today = moment(),
) => {
  const { arrivalDate: arrivalDateString = '', seasonSuffix = '' } = bookingSearch;
  if (!arrivalDateString) return true;
  const {
    REACT_APP_ARRIVAL_TIME_EDIT_CUTOFF = 0,
    [`REACT_APP_ARRIVAL_NUMBER_OF_DAYS_BEFORE_BREAK${seasonSuffix}`]: configArrivalNumberOfDaysBeforeBreak = 0,
  } = config;
  const arrivalTimeEditCutoff = parseInt(REACT_APP_ARRIVAL_TIME_EDIT_CUTOFF, 10);
  const arrivalNumberOfDaysBeforeBreak = parseInt(configArrivalNumberOfDaysBeforeBreak, 10);
  const arrivalDate = moment(arrivalDateString).hours('13').minutes('0');

  const arrivalDateWithCutoff = moment(arrivalDate).subtract(arrivalTimeEditCutoff, 'hours');
  const arrivalDateAndTodayDiff = moment(arrivalDate).diff(today, 'day');

  return today.isBefore(arrivalDateWithCutoff) && arrivalDateAndTodayDiff <= arrivalNumberOfDaysBeforeBreak;
};

const getSelectedArrivalTime = ({ arrivalTimeSlots = [], bookedArrivalSlotId }) => {
  if (!bookedArrivalSlotId) return {};
  const [selectedSlot = {}] = arrivalTimeSlots.filter(({ code }) => code === bookedArrivalSlotId);
  return selectedSlot;
};

export const getEntertainmentDates = (startDate, stopDate, isVenues = false, today = moment()) => {
  const dateArray = [];
  let currentMomentDate = today.isSameOrAfter(moment(startDate), 'day') ? today : moment(startDate);
  const stopMomentDate = moment(stopDate);
  while (currentMomentDate.isSameOrBefore(stopMomentDate, 'day')) {
    const formattedCurrentMomentDate = moment(currentMomentDate).format('YYYY-MM-DD');
    if (isVenues) {
      dateArray.push({
        date: formattedCurrentMomentDate,
        venues: [],
      });
    } else {
      dateArray.push({
        date: formattedCurrentMomentDate,
        events: [],
      });
    }
    currentMomentDate = moment(currentMomentDate).add(1, 'days');
  }
  return dateArray;
};

export const checkIfTravelAgentBooking = agencyType => agencyType === 'TRAVEL AGENT';

const getIsInEveningTimeWindow = (start, selectedDate, startHour, startMinute, endHour, endMinute) => {
  const windowStart = moment(selectedDate).hour(startHour).minute(startMinute);
  const windowEnd = moment(selectedDate).hour(endHour).minute(endMinute);
  return start.isBetween(windowStart, windowEnd, undefined, '[]');
};

export const getNumOfBookedEveningShowsOnCertainDay = (
  bookedEntertainments = [],
  singleDate,
  startHour,
  startMinute,
  endHour,
  endMinute,
) => bookedEntertainments
  .filter(({ ActivityName, Time, isDineAround, isBMEItem }) => {
    const start = moment(Time);
    const selectedDate = moment(singleDate);
    const isSameDay = start.isSame(selectedDate, 'day');
    return isSameDay
      && getIsInEveningTimeWindow(start, singleDate, startHour, startMinute, endHour, endMinute)
      && !isSplash(ActivityName)
      && !isDineAround
      && isBMEItem;
  }).length;

export const getIfAllowedToBookEveningShowSlot = (
  { singleDate = '' },
  bookedEntertainments,
  isItTargetCategory,
  eveningShowRestrictionStartDate = '',
  eveningShowRestrictionEndDate = '',
  eveningShowRestrictionStartTime = '',
  eveningShowRestrictionEndTime = '',
  additionalEveningShowTime = '',
) => {
  const [startHour = '0', startMinute = '0'] = eveningShowRestrictionStartTime.split(':');
  const [endHour = '0', endMinute = '0'] = eveningShowRestrictionEndTime.split(':');

  const numOfBookedEveningShowsOnThisDay = getNumOfBookedEveningShowsOnCertainDay(
    bookedEntertainments,
    singleDate,
    startHour,
    startMinute,
    endHour,
    endMinute,
  );

  const start = moment(singleDate);
  const isInEveningDateWindow = start.isBetween(moment(eveningShowRestrictionStartDate), moment(eveningShowRestrictionEndDate), 'day', '[]');
  const isInEveningTimeWindow = getIsInEveningTimeWindow(start, singleDate, startHour, startMinute, endHour, endMinute);
  if (!isItTargetCategory || !isInEveningDateWindow || !isInEveningTimeWindow || numOfBookedEveningShowsOnThisDay === 0) return true;
  const [additionalHour = '0', additionalMinute = '0'] = additionalEveningShowTime.split(':');
  const isAdditionalEveningShowWindowOpened = moment().isAfter(moment(singleDate).hour(additionalHour).minute(additionalMinute));
  return isAdditionalEveningShowWindowOpened;
};

export const convertTimeSlotsToOptions = (slots = [], duration = 0) => {
  if (!slots) {
    return [];
  }
  return slots.map(({ Starts: startDate, Id, RemainingCapacity }) => ({
    singleDate: startDate,
    code: Id,
    remainingCapacity: RemainingCapacity,
    duration,
  }));
};

export const updateBookedEntertainments = async (resId, siteCode, getBookedEntertainments, getAvailableEntertainments) => {
  await getBookedEntertainments(resId, siteCode);
  await getAvailableEntertainments(siteCode);
};

export const itWasBookedItem = (bookedEntertainmentsWithoutPastEvents = [], code = '') => {
  if (!bookedEntertainmentsWithoutPastEvents || bookedEntertainmentsWithoutPastEvents.length === 0 || !code) {
    return false;
  }
  const selectedCode = bookedEntertainmentsWithoutPastEvents.filter(({ TimeSlotId: timeSlotId }) => timeSlotId === code);
  if (selectedCode.length > 0) return true;
  return false;
};

export const getSelectedEntertainment = (filteredEvent, openVenuesForBreakfast = null, openVenuesForDinner = null) => {
  const { slots = [], duration = 0, isDineAroundItem = false, dineAroundType } = filteredEvent;
  const newSlots = convertTimeSlotsToOptions(slots, duration);
  if (!isDineAroundItem) {
    return {
      ...filteredEvent,
      slots: newSlots,
    };
  }
  return {
    ...filteredEvent,
    slots: newSlots,
    openVenues: dineAroundType === dineAroundBreakfastType ? openVenuesForBreakfast : openVenuesForDinner,
  };
};

export const deleteEntertainmentsInCategory = ({
  selectedEntertainment,
  resId,
  siteCode,
  deleteEntertainment,
  bookedEntertainmentsWithoutPastEvents = [],
  skipToReportToGTM = false,
  selectedTimeslots = [],
  isCancel,
}) => {
  const { slots: slotsOfSelectedActivity = [] } = selectedEntertainment;
  const slotIdsOfSelectedActivity = slotsOfSelectedActivity.map(({ code }) => code);
  const bookedSlotIds = bookedEntertainmentsWithoutPastEvents.map(({ TimeSlotId }) => TimeSlotId);
  const idsToDelete = !isCancel
    ? slotIdsOfSelectedActivity.filter(slotId => bookedSlotIds.includes(slotId) && !selectedTimeslots.includes(slotId))
    : slotIdsOfSelectedActivity.filter(slotId => bookedSlotIds.includes(slotId));

  const { slots = [], duration, name } = selectedEntertainment;
  const deletePromises = slots
    .filter(({ code }) => idsToDelete.includes(code))
    .map(({ code, singleDate: selectedFullDate }) => {
      const eventDate = moment(selectedFullDate).format('YYYY-MM-DD');
      const startTime = moment(selectedFullDate).format('HH:mm');
      const endTime = moment(selectedFullDate).add(duration, 'minutes').format('HH:mm');
      return deleteEntertainment(
        resId, siteCode, code, eventDate, startTime, endTime, name, skipToReportToGTM ? false : itWasBookedItem(bookedEntertainmentsWithoutPastEvents, code),
      );
    });
  return Promise.all(deletePromises);
};

export const handleDeleteEntertainment = async (
  selectedEntertainment,
  resId,
  siteCode,
  deleteEntertainment,
  getBookedEntertainments,
  getAvailableEntertainments,
  bookedEntertainmentsWithoutPastEvents = [],
  timeSlotId,
) => {
  if (timeSlotId) {
    const { duration, name, slots = [] } = selectedEntertainment;
    const [{ singleDate: selectedFullDate }] = slots.filter(({ code }) => code === timeSlotId);
    const eventDate = moment(selectedFullDate).format('YYYY-MM-DD');
    const startTime = moment(selectedFullDate).format('HH:mm');
    const endTime = moment(selectedFullDate).add(duration, 'minutes').format('HH:mm');
    await deleteEntertainment(resId, siteCode, timeSlotId, eventDate, startTime, endTime, name, true);
  } else {
    await deleteEntertainmentsInCategory({
      selectedEntertainment,
      resId,
      siteCode,
      deleteEntertainment,
      bookedEntertainmentsWithoutPastEvents,
      isCancel: true,
    });
  }
  await updateBookedEntertainments(resId, siteCode, getBookedEntertainments, getAvailableEntertainments);
};

export const IsSelectedEventExpired = (bookedSlotsOfThisEvent = [], options = []) => {
  if (bookedSlotsOfThisEvent.length === 0 || options.length === 0) return false;
  let isExpired = false;
  options.forEach(({ code = '', singleDate = '', duration = 0 }) => {
    if (bookedSlotsOfThisEvent.includes(code) && duration > 0) {
      isExpired = isCurrentTimeAfterEvent(singleDate, duration);
    }
  });
  return isExpired;
};

export const areAllCheckedSlotsAlreadyBooked = (bookedSlotsOfThisEvent, checked) => {
  let allBooked = true;
  checked.forEach(({ slotId }) => {
    if (!bookedSlotsOfThisEvent.includes(slotId)) allBooked = false;
  });
  return allBooked;
};

export const getArrayWithUniqueElements = (array) => {
  const uniqueElements = [...new Set(array.map(({ slotId }) => slotId))];
  return uniqueElements.map((slotId) => {
    const { guestNumber } = array.find(slot => slot.slotId === slotId);
    return { slotId, guestNumber };
  });
};

const getValidTime = (time = '6:00') => {
  const [hourString, minuteString] = time.split(':');
  if (hourString.length === 1) return `0${hourString}:${minuteString}:00`;
  return `${hourString}:${minuteString}:00`;
};

export const getClashedBookedEntertainments = (singleDate, duration, code, bookedEntertainmentsOnThisDay, overlapCheckPoolItems, overlapAllowedMinutes) => {
  const clashedEvents = [];
  if (!singleDate || !code || !duration || !bookedEntertainmentsOnThisDay || bookedEntertainmentsOnThisDay.length === 0 || !overlapCheckPoolItems) {
    return clashedEvents;
  }
  const extendedMoment = extendMoment(moment);
  const checkedSlotStartTime = moment(singleDate);
  const checkedSlotEndTime = moment(singleDate).add(duration, 'minute');
  const checkedSlotRange = extendedMoment.range(checkedSlotStartTime, checkedSlotEndTime);
  const bookedBMEItems = bookedEntertainmentsOnThisDay.filter(({ isBMEItem }) => isBMEItem);
  bookedBMEItems.forEach((bookedEntertainment) => {
    const { id: timeSlotId, startDate, startTime, endTime, name } = bookedEntertainment;
    if (parseInt(timeSlotId, 10) !== code) {
      const extendedBookedStartTimeWithDate = moment(`${startDate}T${getValidTime(startTime)}`);
      const extendedBookedEndTimeWithDate = moment(`${startDate}T${getValidTime(endTime)}`);
      const bookedTimeRange = extendedMoment.range(extendedBookedStartTimeWithDate, extendedBookedEndTimeWithDate);
      const bookedEventDuration = bookedTimeRange.diff('minute');
      if (bookedTimeRange.overlaps(checkedSlotRange)) {
        const subtractOfRanges = bookedTimeRange.subtract(checkedSlotRange);
        let overlapMinutes = duration;
        if (subtractOfRanges && subtractOfRanges.length > 0) {
          const [{ start: overlapStartTime, end: overlapEndTime }] = bookedTimeRange.subtract(checkedSlotRange);
          overlapMinutes = bookedEventDuration - overlapEndTime.diff(overlapStartTime, 'minute');
        }
        if ((overlapCheckPoolItems || (!overlapCheckPoolItems && !isSplash(name))) && overlapAllowedMinutes < overlapMinutes) {
          clashedEvents.push(bookedEntertainment);
        }
      }
    }
  });
  return clashedEvents;
};

export const getEventsOnThisDay = (bookedEvents, singleDate) => bookedEvents.filter(({ startDate }) => moment(singleDate).isSame(moment(startDate), 'day'));

export const getRemovableEntertainmentDateLabel = (bookedEvents, type = 'splash', selectedActivityId, isOnlyOne) => {
  if (!isOnlyOne) return '';
  const [entertainmentToDelete = {}] = bookedEvents.filter(({ name, activityId }) => (type === 'splash' ? isSplash(name) : activityId === selectedActivityId));
  const { startDate: startDateString, startTime } = entertainmentToDelete;
  const startDate = moment(startDateString);
  const dayOfWeek = startDate.format('dddd');
  return `${dayOfWeek.toUpperCase()} • ${startDate.format('D MMMM')} - ${startTime}`;
};

export const getIsChecked = (checked, code) => checked.filter(({ slotId }) => slotId === code).length > 0;

const filterEmptyDays = (entertainments = []) => entertainments.filter(({ events }) => events.length > 0);

export const getShouldSkipSwimmingOrEntertainment = (wasCalled, items) => wasCalled
  && (!items || items.length === 0 || filterEmptyDays(items).length === 0);

export const handleDeleteClashedEvent = async (
  deleteEntertainment,
  resId,
  siteCode,
  {
    id,
    startDate: eventDate,
    startTime,
    endTime,
    name,
  }) => {
  const timeSlotId = parseInt(id, 10);
  await deleteEntertainment(resId, siteCode, timeSlotId, eventDate, startTime, endTime, name, true);
};

export const deleteEntertainmentByType = (deleteEntertainment, bookedEvents, resId, siteCode, type = 'splash', selectedActivityId) => {
  const [entertainmentToDelete = {}] = bookedEvents.filter(({ name, activityId }) => (type === 'splash' ? isSplash(name) : activityId === selectedActivityId));
  const { id = '', startDate = '', startTime = '', endTime = '', name = '' } = entertainmentToDelete;
  return deleteEntertainment(resId, siteCode, id, startDate, startTime, endTime, name, true);
};

export const isExistsSameEntertainment = (selectedActivityId = '', name = '', bookedEntertainmentsWithoutPastEvents = []) => {
  if (selectedActivityId === '' || !bookedEntertainmentsWithoutPastEvents || bookedEntertainmentsWithoutPastEvents.length === 0 || !name) {
    return false;
  }
  if (isSplash(name)) return bookedEntertainmentsWithoutPastEvents.filter(({ ActivityName }) => ActivityName.toLowerCase() === name.toLowerCase()).length > 0;
  return bookedEntertainmentsWithoutPastEvents.filter(({ activityId }) => activityId === selectedActivityId).length > 0;
};

export const getArrivalStartTime = (arrivalTime) => {
  const { startTime } = getSelectedArrivalTime(arrivalTime);
  return startTime;
};

export const getArrivalEndTime = (arrivalTime) => {
  const { endTime } = getSelectedArrivalTime(arrivalTime);
  return endTime;
};

export const getSlotColor = (index) => {
  const indexNum = parseInt(index, 10);
  if (indexNum === 0) return 'slot-color-1';
  const colorIndex = (indexNum % 8) + 1;
  return `slot-color-${colorIndex}`;
};

export const getIfAllowedToAccessItems = (
  isTravelAgentBooking,
  isSecretCodeApplied,
  isBeforeBreak,
  isDineAroundBooking,
  isArrivalDateBeforeRestrictionDate,
  isArrivalDateAfterLastBookingDate,
) => {
  const access = { isAllowedToBookDineAround: isDineAroundBooking, isAllowedToBookSwimming: true, isAllowedToBookEntertainment: true };
  if (isArrivalDateBeforeRestrictionDate) return access;
  if (!isTravelAgentBooking && isBeforeBreak && !isSecretCodeApplied && isArrivalDateAfterLastBookingDate) {
    access.isAllowedToBookSwimming = false;
    access.isAllowedToBookEntertainment = false;
  }
  return access;
};

export const getIfAllowedToBook = ({ arrivalDate, agencyType, isDaBooking, secretCode, siteCode, seasonSuffix }, config) => {
  const { REACT_APP_TEAM_ACCESS_OVERRIDE_PASSCODE, REACT_APP_RESTRICT_BME_ITEM_ACCESS_START_DATE } = config;
  const isTravelAgentBooking = checkIfTravelAgentBooking(agencyType);
  const isSecretCodeApplied = REACT_APP_TEAM_ACCESS_OVERRIDE_PASSCODE === secretCode;
  const isBeforeBreak = moment().isBefore(arrivalDate, 'day');
  const isArrivalDateBeforeRestrictionDate = moment(arrivalDate).isBefore(REACT_APP_RESTRICT_BME_ITEM_ACCESS_START_DATE, 'day');
  const activityBookingLastArrivalDate = config[`REACT_APP_ACTIVITY_BOOKING_LAST_ARRIVAL_DATE_${siteCode.toUpperCase()}${seasonSuffix}`];
  const isArrivalDateAfterLastBookingDate = moment(arrivalDate).isAfter(activityBookingLastArrivalDate, 'day');

  return getIfAllowedToAccessItems(
    isTravelAgentBooking,
    isSecretCodeApplied,
    isBeforeBreak,
    isDaBooking,
    isArrivalDateBeforeRestrictionDate,
    isArrivalDateAfterLastBookingDate,
  );
};

export const shouldFetchAvailableItems = (
  availableEntertainments,
  bookingSearch,
  config,
  bookedEntertainment,
) => {
  const { isFetching, wasCalled } = bookedEntertainment;
  return shouldFetch(availableEntertainments, bookingSearch, config) && !isFetching && wasCalled;
};

export const getIfArrivalDateIsBeforeLatestArrivalSlotBookingDate = ({ arrivalDate, seasonSuffix = '' }, config, today = moment()) => {
  const {
    [`REACT_APP_ARRIVAL_SLOT_LATEST_BOOKING_DATE${seasonSuffix}`]: arrivalSlotLatestBookingDate,
    [`REACT_APP_ARRIVAL_NUMBER_OF_DAYS_BEFORE_BREAK${seasonSuffix}`]: configArrivalNumberOfDaysBeforeBreak = 0,
  } = config;
  const momentArrivalDate = moment(arrivalDate);
  const arrivalNumberOfDaysBeforeBreak = parseInt(configArrivalNumberOfDaysBeforeBreak, 10);
  if (!arrivalSlotLatestBookingDate || !moment(arrivalSlotLatestBookingDate).isValid()) {
    const arrivalDateTime = momentArrivalDate.hours('13').minutes('0');
    const arrivalDateAndTodayDiff = moment(arrivalDateTime).diff(today, 'day');
    return arrivalDateAndTodayDiff <= arrivalNumberOfDaysBeforeBreak;
  }
  return momentArrivalDate.isBefore(arrivalSlotLatestBookingDate);
};

export const checkIsConfigNeedsToBeRefreshed = (lastConfigCheckDateTime, refreshTimeInterval, now = moment()) => {
  const momentLastConfigCheckDateTime = moment(lastConfigCheckDateTime);
  const diffBetweenLastCheckedDateAndNow = now.diff(momentLastConfigCheckDateTime, 'minute');
  return diffBetweenLastCheckedDateAndNow >= refreshTimeInterval;
};

export const redirectUserToSchedulePageWithReload = () => {
  const { location = {} } = window;
  const { href: currentLocation, search } = location;
  const locationWithoutQueryParams = currentLocation.replace(search, '');
  window.location.replace(`${locationWithoutQueryParams}${schedulePage}`);
};

export const getSelectedActivityByCode = (entertainments = [], entertainmentId = '') => {
  const allEvents = entertainments.reduce((accumulator, { events = [] }) => [...accumulator, ...events], []);
  return allEvents.find(({ code }) => code === entertainmentId) || {};
};

export const getIsPerPersonActivity = ({ REACT_APP_PER_PERSON_ACTIVITY_IDS = '' }, activityId) => {
  const perPersonActivities = REACT_APP_PER_PERSON_ACTIVITY_IDS
    .split(',')
    .map((id) => {
      const trimmedId = id.trim();
      return parseInt(trimmedId, 10);
    })
    .filter(id => !!id);

  if (!activityId) return false;
  return perPersonActivities.includes(activityId);
};

const getMomentFromTimeString = (timeString) => {
  const { hours, minutes } = getHoursAndMinutes(timeString);
  return moment().hours(hours).minutes(minutes);
};

const isBeforeCutoff = (dateOfSelectedActivity, cutoff) => {
  if (moment().isAfter(dateOfSelectedActivity, 'days')) return false;
  if (moment().isBefore(dateOfSelectedActivity, 'days')) return true;
  const cutoffTime = getMomentFromTimeString(cutoff);
  return moment().isBefore(cutoffTime);
};

export const getIsAllowedToBookDABasedOnCutoff = (
  breakfastCutoff,
  afternoonCutoff,
  afternoonStartTime,
  afternoonEndTime,
  eveningCutoff,
  eveningStartTime,
  eveningEndTime,
  startOfDA,
) => {
  if (moment(startOfDA).isBefore(moment())) return false;
  if (moment().isBefore(startOfDA, 'days')) return true;

  const eveningStart = getMomentFromTimeString(eveningStartTime);
  const eveningEnd = getMomentFromTimeString(eveningEndTime);
  if (moment(startOfDA).isBetween(eveningStart, eveningEnd, undefined, '[]')) return isBeforeCutoff(startOfDA, eveningCutoff);

  const afternoonStart = getMomentFromTimeString(afternoonStartTime);
  const afternoonEnd = getMomentFromTimeString(afternoonEndTime);
  if (moment(startOfDA).isBetween(afternoonStart, afternoonEnd, undefined, '[]')) return isBeforeCutoff(startOfDA, afternoonCutoff);

  return isBeforeCutoff(startOfDA, breakfastCutoff);
};

export const isBeforeBreak = (arrivalDate, poolRestrictionTime) => !isNowAfterTime(arrivalDate, poolRestrictionTime);

export const hasReachedPreBreakPoolMaxSlots = (
  {
    REACT_APP_MAX_PRE_ARRIVAL_POOL_SLOTS_LESS_THAN_7_NIGHTS: maxPreArrivalPoolSlotsLessThan7Nights,
    REACT_APP_MAX_PRE_ARRIVAL_POOL_SLOTS_MORE_THAN_OR_EQUAL_7_NIGHTS: maxPreArrivalPoolSlotsMoreThanOrEqual7Nights,
  },
  breakLength,
  numOfBookedPoolActivities,
) => {
  const numOfMaxPreArrivalSlots = breakLength < 7 ? maxPreArrivalPoolSlotsLessThan7Nights : maxPreArrivalPoolSlotsMoreThanOrEqual7Nights;
  return numOfBookedPoolActivities >= numOfMaxPreArrivalSlots;
};

const hasReachedDailyMaxPoolSlots = (
  { REACT_APP_MAX_SWIMMING_ON_SAME_DAY: dailySwimmingMaximum },
  numOfBookedPoolActivities,
) => numOfBookedPoolActivities >= parseInt(dailySwimmingMaximum, 10);

export const isAfterDailyOpeningTime = ({ REACT_APP_DAILY_SWIMMING_OPENING_TIME: swimmingDailyOpeningTime }) => isNowAfterMoment(swimmingDailyOpeningTime);

// const isBookingWindowOpen = (config, arrivalDate, seasonSuffix = '') => {
//   const { [`REACT_APP_NUMBER_OF_DAYS_BEFORE_BREAK${seasonSuffix}`]: numberOfDaysBeforeBookingWindowOpen } = config;
//   const momentArrivalDate = moment(arrivalDate);
//   const bookingWindowOpenDate = momentArrivalDate.subtract(parseInt(numberOfDaysBeforeBookingWindowOpen, 10), 'days');
//   return moment().isSameOrAfter(bookingWindowOpenDate);
// };

const getNumberOfSwimmingBookingsOnToday = (bookedEvents = []) => bookedEvents.filter(
  ({ name, startDate }) => moment(startDate).isSame(moment(), 'day') && isSplash(name),
).length;

export const getNumberOfSwimmingBookingsOnEntireBreak = (bookedEvents = []) => bookedEvents.filter(
  ({ name }) => isSplash(name),
).length;

export const isAllowedToBookMorePoolSlotsOnBreak = (
  config,
  breakLength,
  bookedEvents = [],
  // eslint-disable-next-line no-unused-vars
  seasonSuffix = '',
) => {
  const numOfBookedPoolActivitiesOnToday = getNumberOfSwimmingBookingsOnToday(bookedEvents);
  const numOfBookedPoolActivitiesOnEntireBreak = getNumberOfSwimmingBookingsOnEntireBreak(bookedEvents);
  /* if (isBeforeBreak(arrivalDate)) { // PRE-BREAK
    if (isBookingWindowOpen(config, arrivalDate, seasonSuffix)) {
      return !hasReachedPreBreakPoolMaxSlots(config, breakLength, numOfBookedPoolActivitiesOnEntireBreak);
    }
    return false;
  } // ON BREAK */
  if (hasReachedPreBreakPoolMaxSlots(config, breakLength, numOfBookedPoolActivitiesOnEntireBreak)) {
    if (isAfterDailyOpeningTime(config)) {
      return !hasReachedDailyMaxPoolSlots(config, numOfBookedPoolActivitiesOnToday);
    }
    return false;
  }
  return true;
};

export const getBreakLength = (arrivalDateString, departureDateString) => {
  const arrivalDate = moment(arrivalDateString);
  const departureDate = moment(departureDateString);
  return departureDate.startOf('day').diff(arrivalDate.startOf('day'), 'days');
};

export const getMaxAllowedSlots = (
  arrivalDateString,
  departureDateString,
  maxAllowedSlotsForLessThan7Nights,
  maxAllowedSlotsForMoreThanOrEqual7Nights,
) => {
  const breakLength = getBreakLength(arrivalDateString, departureDateString);
  const maxAllowed = breakLength < 7 ? maxAllowedSlotsForLessThan7Nights : maxAllowedSlotsForMoreThanOrEqual7Nights;
  return parseInt(maxAllowed, 10);
};

export const getMaxAllowedPoolSlots = (
  arrivalDateString,
  departureDateString,
  {
    REACT_APP_LIFT_POOL_RESTRICTION_TIME: poolRestrictionTime,
    REACT_APP_MAX_SWIMMING_ON_SAME_DAY: maxSwimmingOnSameDay,
    REACT_APP_MAX_PRE_ARRIVAL_POOL_SLOTS_LESS_THAN_7_NIGHTS: maxPreArrivalPoolSlotsLessThan7Nights,
    REACT_APP_MAX_PRE_ARRIVAL_POOL_SLOTS_MORE_THAN_OR_EQUAL_7_NIGHTS: maxPreArrivalPoolSlotsMoreThanOrEqual7Nights,
    REACT_APP_DAILY_SWIMMING_OPENING_TIME: swimmingDailyOpeningTime,
  },
  bookedEvents = [],
) => {
  const maxAllowedPoolSlotsForPreBreak = getMaxAllowedSlots( // PRE-BREAK
    arrivalDateString,
    departureDateString,
    maxPreArrivalPoolSlotsLessThan7Nights,
    maxPreArrivalPoolSlotsMoreThanOrEqual7Nights,
  );
  if (isNowAfterTime(arrivalDateString, poolRestrictionTime)) { // ON BREAK
    const numOfBookedPoolActivitiesOnEntireBreak = getNumberOfSwimmingBookingsOnEntireBreak(bookedEvents);
    if (numOfBookedPoolActivitiesOnEntireBreak < maxAllowedPoolSlotsForPreBreak
      || !isAfterDailyOpeningTime({ REACT_APP_DAILY_SWIMMING_OPENING_TIME: swimmingDailyOpeningTime })) return maxAllowedPoolSlotsForPreBreak;
    const breakLength = getBreakLength(arrivalDateString, departureDateString);
    return maxSwimmingOnSameDay * breakLength;
  }
  return maxAllowedPoolSlotsForPreBreak; // PRE-BREAK
};

export const getSwimmingFreeFlowMessage = (originalSplash, splashFilteredByFreeFlow) => {
  if (splashFilteredByFreeFlow.length === 0 && originalSplash.length !== 0) return 'Swimming is free flow on your break';
  if (splashFilteredByFreeFlow.length < originalSplash.length) return 'Swimming is free flow on the remaining days of your break';
  return '';
};

export const filterSplashByFreeFlowStartDate = (formattedSplashItems, swimmingFreeFlowStartDate) => formattedSplashItems.filter(({ date }) => {
  if (!swimmingFreeFlowStartDate) return true;
  return moment(date).isBefore(swimmingFreeFlowStartDate, 'day');
});

export const getIsHBSBooker = ({
  REACT_APP_HBS_USE_BOOKING_SOURCE,
  REACT_APP_HBS_BOOKING_SOURCE = '',
  REACT_APP_HBS_REFERRAL_CODES = '',
}, hbsCodeFromApi) => {
  const codesFromConfig = REACT_APP_HBS_USE_BOOKING_SOURCE === 'true' ? REACT_APP_HBS_BOOKING_SOURCE : REACT_APP_HBS_REFERRAL_CODES;
  if (codesFromConfig === '' || hbsCodeFromApi === '') return false;
  const bookingSourceCodes = codesFromConfig.split(',');
  return bookingSourceCodes.some(code => hbsCodeFromApi.includes(code));
};

export const getIsDABooker = (addons = [], dineAroundAddonCodes) => { 
  let checkAddon = false;
  if (!addons || addons.length === 0 || dineAroundAddonCodes === '') {
    return checkAddon;
  }
  const daAddonCodes = dineAroundAddonCodes.split(',').map(code => code.trim());
  console.log(daAddonCodes);
 return addons.some(({ addon }) => daAddonCodes.includes(addon.code));


};

const getIfNumberOfDaysBeforeBreakPopulated = (config, seasonSuffix = '') => {
  const { [`REACT_APP_NUMBER_OF_DAYS_BEFORE_BREAK${seasonSuffix}`]: numberOfDaysBeforeBreak } = config;
  return !!numberOfDaysBeforeBreak
    && Number.isInteger(parseInt(numberOfDaysBeforeBreak, 10));
};

const areGlobalOpeningFixedDatesVariablesPopulated = (config, seasonSuffix = '') => {
  const { [`REACT_APP_LATEST_ARRIVAL_DATE${seasonSuffix}`]: latestArrivalDate } = config;
  return !!latestArrivalDate && moment(latestArrivalDate).isValid();
};

const getGlobalOpeningFixedDatesMainOpenWindow = (config, seasonSuffix = '') => {
  const { [`REACT_APP_LATEST_ARRIVAL_DATE${seasonSuffix}`]: latestArrivalDate } = config;
  return moment(latestArrivalDate).format('YYYY-MM-DD');
};

const areGlobalRollingOpeningVariablesPopulated = (config, siteCode, seasonSuffix = '') => {
  const {
    [`REACT_APP_ACTIVITY_BOOKING_TIME_SETTINGS_ENABLED${seasonSuffix}`]: activityBookingTimeSettingsEnabled,
    [`REACT_APP_ACTIVITY_BOOKING_START_TIME_ROLLING_GLOBAL${seasonSuffix}`]: startTimeRolling,
    
  } = config;
  const isActivityBookingTimeSettingsEnabledPopulated = !!activityBookingTimeSettingsEnabled
    && (activityBookingTimeSettingsEnabled === 'true' || activityBookingTimeSettingsEnabled === 'false');
  const isNumberOfDaysBeforeBreakPopulated = getIfNumberOfDaysBeforeBreakPopulated(config, seasonSuffix);
  const isActivityBookingStartTimeRollingPopulated = !!startTimeRolling;

  return isNumberOfDaysBeforeBreakPopulated && (!isActivityBookingTimeSettingsEnabledPopulated || !isActivityBookingStartTimeRollingPopulated);
};

const getGlobalRollingOpeningMainOpenWindow = (config, seasonSuffix = '') => {
  const { [`REACT_APP_NUMBER_OF_DAYS_BEFORE_BREAK${seasonSuffix}`]: numberOfDaysBeforeBreak } = config;
  return moment()
    .add(numberOfDaysBeforeBreak, 'days')
    .format('YYYY-MM-DD');
};

const areRollingOpeningByResortVariablesPopulated = (config, siteCode, seasonSuffix = '') => {
  const {
    [`REACT_APP_ACTIVITY_BOOKING_TIME_SETTINGS_ENABLED${seasonSuffix}`]: activityBookingTimeSettingsEnabled,
    [`REACT_APP_ACTIVITY_BOOKING_START_TIME_ROLLING_GLOBAL${seasonSuffix}`]: startTimeRolling,
  } = config;
  const isActivityBookingTimeSettingsEnabledPopulated = !!activityBookingTimeSettingsEnabled
    && (activityBookingTimeSettingsEnabled === 'true' || activityBookingTimeSettingsEnabled === 'false');
  const isNumberOfDaysBeforeBreakPopulated = getIfNumberOfDaysBeforeBreakPopulated(config, seasonSuffix);
  const isActivityBookingStartTimeRollingPopulated = !!startTimeRolling;

  return isActivityBookingTimeSettingsEnabledPopulated && isNumberOfDaysBeforeBreakPopulated && isActivityBookingStartTimeRollingPopulated;
};

const getRollingOpeningByResortMainOpenWindow = (config, seasonSuffix = '') => {
  const { [`REACT_APP_NUMBER_OF_DAYS_BEFORE_BREAK${seasonSuffix}`]: numberOfDaysBeforeBreak } = config;
  return moment()
    .add(numberOfDaysBeforeBreak, 'days')
    .format();
};

const isBreakOpenBasedOnRollingOpeningByResort = (config, arrivalDate, siteCode, mainOpenWindow, seasonSuffix = '') => {
  const {
    [`REACT_APP_ACTIVITY_BOOKING_TIME_SETTINGS_ENABLED${seasonSuffix}`]: activityBookingTimeSettingsEnabled,
    [`REACT_APP_ACTIVITY_BOOKING_START_TIME_ROLLING_GLOBAL${seasonSuffix}`]: startTimeRollingDate,
  } = config;

  if (activityBookingTimeSettingsEnabled !== 'true') return false;

  const { hours: rollingHour, minutes: rollingMinute } = getHoursAndMinutes(startTimeRollingDate);
  const arrivalDateTime = moment(arrivalDate).hour(rollingHour).minute(rollingMinute).second(0);

  return moment(arrivalDateTime).isSameOrBefore(mainOpenWindow, 'minute');
};

const areHBSEarlyAccessVariablesPopulated = ({
  REACT_APP_EARLY_ACCESS_STARTDATE,
  REACT_APP_EARLY_ACCESS_ENDDATE,
  REACT_APP_NUMBER_OF_DAYS_BEFORE_MAIN_OPENING,
  REACT_APP_HBS_ACCESS_BOOKING_DATE_START,
  REACT_APP_HBS_ACCESS_BOOKING_DATE_END,
}) => !!REACT_APP_EARLY_ACCESS_STARTDATE && moment(REACT_APP_EARLY_ACCESS_STARTDATE).isValid()
&& !!REACT_APP_EARLY_ACCESS_ENDDATE && moment(REACT_APP_EARLY_ACCESS_ENDDATE).isValid()
&& !!REACT_APP_HBS_ACCESS_BOOKING_DATE_START && moment(REACT_APP_HBS_ACCESS_BOOKING_DATE_START).isValid()
&& !!REACT_APP_HBS_ACCESS_BOOKING_DATE_END && moment(REACT_APP_HBS_ACCESS_BOOKING_DATE_END).isValid()
&& !!REACT_APP_NUMBER_OF_DAYS_BEFORE_MAIN_OPENING && Number.isInteger(parseInt(REACT_APP_NUMBER_OF_DAYS_BEFORE_MAIN_OPENING, 10));

const isInEarlyAccessWindow = (
  {
    REACT_APP_EARLY_ACCESS_STARTDATE,
    REACT_APP_EARLY_ACCESS_ENDDATE,
    REACT_APP_NUMBER_OF_DAYS_BEFORE_MAIN_OPENING,
    REACT_APP_HBS_ACCESS_BOOKING_DATE_START,
    REACT_APP_HBS_ACCESS_BOOKING_DATE_END,
  },
  arrivalDate,
  mainOpenWindow,
  bookingDate,
  startTimeRollingDate,
) => {
  if (!moment(arrivalDate).isBetween(REACT_APP_EARLY_ACCESS_STARTDATE, REACT_APP_EARLY_ACCESS_ENDDATE, 'day', '[]')) return false;
  if (
    !!bookingDate
    && moment(bookingDate).isValid()
    && !moment(bookingDate).isBetween(REACT_APP_HBS_ACCESS_BOOKING_DATE_START, REACT_APP_HBS_ACCESS_BOOKING_DATE_END, 'day', '[]')
  ) return false;
  const hbsWindow = moment(mainOpenWindow).add(REACT_APP_NUMBER_OF_DAYS_BEFORE_MAIN_OPENING, 'days');
  const { hours: rollingHour, minutes: rollingMinute } = getHoursAndMinutes(startTimeRollingDate);
  const arrivalDateTime = moment(arrivalDate).hour(rollingHour).minute(rollingMinute).second(0);
  return moment(arrivalDateTime).isSameOrBefore(hbsWindow, 'minute');
};

export const getMainOpenWindowData = (config, arrivalDate, siteCode, seasonSuffix = '') => {
  if (areGlobalOpeningFixedDatesVariablesPopulated(config, seasonSuffix)) { // scenario A from 36884
    const mainOpenWindow = getGlobalOpeningFixedDatesMainOpenWindow(config, seasonSuffix);
    return {
      isBreakOpen: moment(arrivalDate).isSameOrBefore(mainOpenWindow, 'day'),
      mainOpenWindow,
    };
  }

  if (areGlobalRollingOpeningVariablesPopulated(config, siteCode, seasonSuffix)) { // scenario B from 36884
    const mainOpenWindow = getGlobalRollingOpeningMainOpenWindow(config, seasonSuffix);
    return {
      isBreakOpen: moment(arrivalDate).isSameOrBefore(mainOpenWindow, 'day'),
      mainOpenWindow,
    };
  }

  if (areRollingOpeningByResortVariablesPopulated(config, siteCode, seasonSuffix)) { // scenario D from 36884
    const mainOpenWindow = getRollingOpeningByResortMainOpenWindow(config, seasonSuffix);
    return {
      isBreakOpen: isBreakOpenBasedOnRollingOpeningByResort(config, arrivalDate, siteCode, mainOpenWindow, seasonSuffix),
      mainOpenWindow,
    };
  }

  return { isBreakOpen: false };
};

// check if it a HBS booking comparing the booking source or referral code
// checks if all the HBS variables are populated
// Checks if it is qualified HBS booking by comparing arrival date with HBS allowed dates
export const isQualifyingHbs = (config, hbsCodeFromApi, arrivalDate, bookingDate, siteCode, seasonSuffix) => {
  const { mainOpenWindow } = getMainOpenWindowData(config, arrivalDate, siteCode, seasonSuffix);
  const { [`REACT_APP_ACTIVITY_BOOKING_START_TIME_ROLLING_GLOBAL${seasonSuffix}`]: startTimeRollingDate } = config;
  return getIsHBSBooker(config, hbsCodeFromApi)
    && areHBSEarlyAccessVariablesPopulated(config)
    && isInEarlyAccessWindow(config, arrivalDate, mainOpenWindow, bookingDate, startTimeRollingDate);
};

export const isQualifyingHbsUsingAddon = (config, isHbs, arrivalDate, bookingDate, siteCode, seasonSuffix) => {
  const { mainOpenWindow } = getMainOpenWindowData(config, arrivalDate, siteCode, seasonSuffix);
  const { [`REACT_APP_ACTIVITY_BOOKING_START_TIME_ROLLING_GLOBAL${seasonSuffix}`]: startTimeRollingDate } = config;
  return isHbs
    && areHBSEarlyAccessVariablesPopulated(config)
    && isInEarlyAccessWindow(config, arrivalDate, mainOpenWindow, bookingDate, startTimeRollingDate);
};

export const getIfBreakOpen = (config, hbsCodeFromApi, arrivalDate, siteCode, bookingDate, seasonSuffix = '') => {
  const { isBreakOpen } = getMainOpenWindowData(config, arrivalDate, siteCode, seasonSuffix);
  if (isBreakOpen) return true;
  return isQualifyingHbs(config, hbsCodeFromApi, arrivalDate, bookingDate, siteCode, seasonSuffix);
};

export const getIfBreakOpenUsingAddon = (config, isHbs, arrivalDate, siteCode, bookingDate, seasonSuffix = '') => {
  const { isBreakOpen } = getMainOpenWindowData(config, arrivalDate, siteCode, seasonSuffix);
  if (isBreakOpen) return true;
  return isQualifyingHbsUsingAddon(config, isHbs, arrivalDate, bookingDate, siteCode, seasonSuffix);
};

export const getShowAppModal = ({
  isTravelAgentBooking,
  isSecretCodeApplied,
  isOnBreak,
  config,
  siteCode,
  arrivalDate,
  seasonSuffix = '',
}) => {
  const { [`REACT_APP_ACTIVITY_BOOKING_LAST_ARRIVAL_DATE_${siteCode.toUpperCase()}${seasonSuffix}`]: activityBookingLastArrivalDate } = config;

  if (isTravelAgentBooking || isSecretCodeApplied || isOnBreak) return false;
  if (!activityBookingLastArrivalDate || !moment(activityBookingLastArrivalDate).isValid()) return false;
  return moment(arrivalDate).isAfter(activityBookingLastArrivalDate, 'day');
};

const isItMondayThursdayPeriod = numberOfDayOnWeek => numberOfDayOnWeek >= 1 && numberOfDayOnWeek <= 4;

const getNumberOfDaysToSubtract = (numberOfDayOnWeek) => {
  const isMondayThursdayPeriod = isItMondayThursdayPeriod(numberOfDayOnWeek);
  if (isMondayThursdayPeriod) return numberOfDayOnWeek - 1;
  if (numberOfDayOnWeek === 0) return 2;
  return numberOfDayOnWeek - 5;
};

const getBackstopDateBasedOnDepartureDate = (backstopDate, departureDate, departureTime) => {
  const { hours, minutes } = getHoursAndMinutes(departureTime);
  if (backstopDate.isBefore(departureDate, 'days')) {
    backstopDate.hour(hours).minute(minutes).second(0);
    return backstopDate.format();
  }

  const departure = moment(departureDate).hour(hours).minute(minutes).second(0);
  return departure.format();
};

const getBackstopDateByChristmas = (date, departureTime) => {
  const { hours, minutes } = getHoursAndMinutes(departureTime);
  return moment(date).hour(hours).minute(minutes).second(0)
    .format();
};

export const getBackstopDate = ({
  config,
  departureDate,
  arrivalDate,
  seasonSuffix = '',
  isEarlyAccess = false,
  today = moment(),  
  backstopDateFromApi
}) => {
  const {
    REACT_APP_BACKSTOP_DAYS_MON_OPENING = '4',
    REACT_APP_BACKSTOP_DAYS_FRI_OPENING = '3',
    REACT_APP_DEPARTURE_TIME: departureTime = '11:00',
    REACT_APP_BACKSTOP_USE_CALENDAR,
    REACT_APP_LATEST_ACTIVITY_BOOKING_DATE,
    REACT_APP_NUMBER_OF_DAYS_BEFORE_MAIN_OPENING,
    [`REACT_APP_NUMBER_OF_DAYS_BEFORE_BREAK${seasonSuffix}`]: configNumberOfDaysBeforeBreak,
    REACT_APP_SPECIALBREAKS,
  } = config;
  const { hours, minutes } = getHoursAndMinutes(departureTime);

  if (moment(REACT_APP_LATEST_ACTIVITY_BOOKING_DATE).isValid()) 
    return moment(REACT_APP_LATEST_ACTIVITY_BOOKING_DATE).hour(hours).minute(minutes).second(0).format();

  if (REACT_APP_BACKSTOP_USE_CALENDAR === 'true') {
    if (moment(REACT_APP_LATEST_ACTIVITY_BOOKING_DATE).isValid()) return REACT_APP_LATEST_ACTIVITY_BOOKING_DATE;
    return backstopDateFromApi;
  }
  const momentArrivalDate = moment(arrivalDate);

  const numberOfDaysBeforeBreak = parseInt(configNumberOfDaysBeforeBreak, 10);
  const latestBreakOpenDate = today.add(numberOfDaysBeforeBreak, 'day');
  const breakLength = getBreakLength(arrivalDate, departureDate);

  if (isEarlyAccess) {
    latestBreakOpenDate.add(REACT_APP_NUMBER_OF_DAYS_BEFORE_MAIN_OPENING, 'day');
  }

  const numberOfDayOnWeek = latestBreakOpenDate.day(); // Originally Sunday is 0.
  const daysToSubtract = getNumberOfDaysToSubtract(numberOfDayOnWeek);
  const adjustedLatestBreakOpenDate = daysToSubtract === 0 ? latestBreakOpenDate : latestBreakOpenDate.subtract(daysToSubtract, 'day');

  const numberOfDayOnWeekOfLatestBreakOpenDate = adjustedLatestBreakOpenDate.day();
  const isLatestBreakOpenDateMonday = numberOfDayOnWeekOfLatestBreakOpenDate === 1;

  if (isEarlyAccess && breakLength === 7) {    
    return moment(departureDate).hour(hours).minute(minutes).second(0).format();
    //return moment(arrivalDate).add(breakLength, 'day').hour(hours).minute(minutes).second(0).format();
  }

  const specialBackstopDate = backStopDateForSpecialBreaks(arrivalDate, breakLength, REACT_APP_SPECIALBREAKS, departureDate);
  if (moment(specialBackstopDate).isValid()) {
    return moment(specialBackstopDate).hour(hours).minute(minutes).second(0).format(); 
  }
  
  if (isLatestBreakOpenDateMonday) {
    return getBackstopDateBasedOnDepartureDate(
      adjustedLatestBreakOpenDate.add(REACT_APP_BACKSTOP_DAYS_MON_OPENING, 'day'),
      departureDate,
      departureTime,
    );
  }

  return getBackstopDateBasedOnDepartureDate(
    adjustedLatestBreakOpenDate.add(REACT_APP_BACKSTOP_DAYS_FRI_OPENING, 'day'),
    departureDate,
    departureTime,
  );
};

const backStopDateForSpecialBreaks = (breakStartDate, holidayDuration, specialBreaks, departureDate) => {
  // Split the special breaks string by commas
  const dateStrings = specialBreaks.split(',');

  // Convert date strings to moment objects
  const dateTimes = dateStrings.map(dateStr => moment(dateStr, 'YYYY-MM-DD'));

  let backstopDate = null;

  // Loop through the array of moment objects
  dateTimes.forEach(dateTime => {
    if (moment(breakStartDate).isSame(dateTime, 'day')) {
      // Add holiday duration to the breakStartDate
      const resultDate = departureDate;
      //const resultDate = moment(breakStartDate).add(holidayDuration, 'days');
      backstopDate = moment(resultDate, 'YYYY-MM-DD'); // Convert to string in 'YYYY-MM-DD' format
    }
  });

  return backstopDate;
};

export const getIfShouldCallBackstopDateEnpoint = ({
  REACT_APP_BACKSTOP_USE_CALENDAR,
  REACT_APP_LATEST_ACTIVITY_BOOKING_DATE,
}) => REACT_APP_BACKSTOP_USE_CALENDAR === 'true' && !moment(REACT_APP_LATEST_ACTIVITY_BOOKING_DATE).isValid();

export const shouldFetchBackstopDate = (config, backstop, bookingSearch) => {
  if (!getIfShouldCallBackstopDateEnpoint(config)) return false;
  const { isFetching, wasCalled } = backstop;
  return shouldFetch(backstop, bookingSearch, config) && !isFetching && !wasCalled;
};

const isActivityAvailableBasedOnBackstopRules = ({
  config,
  departureDate,
  endOfActivity,
  arrivalDate,
  seasonSuffix = '',
  isEarlyAccess = false,
  backstopDateFromApi,
}) => {
  const backstopDate = getBackstopDate({ config, departureDate, arrivalDate, seasonSuffix, isEarlyAccess, backstopDateFromApi });
  return moment(endOfActivity).isBefore(backstopDate);
};

export const filterActivitiesByBackstop = (activities, config, departureDate, arrivalDate, seasonSuffix = '', isEarlyAccess = false, backstopDateFromApi) => activities
  .map((entertainment) => {
    const { events = [] } = entertainment;
    const filteredEvents = events
      .map((event) => {
        const { slots = [], duration = 0 } = event;
        const filteredSlots = slots.filter(({ Starts }) => {
          const finishes = moment(Starts).add(duration, 'minutes').format();
          return isActivityAvailableBasedOnBackstopRules({
            config,
            departureDate,
            endOfActivity: finishes,
            arrivalDate,
            seasonSuffix,
            isEarlyAccess,
            backstopDateFromApi,
          });
        });
        return { ...event, slots: filteredSlots };
      })
      .filter(({ slots }) => !!slots && slots.length > 0);

    return { ...entertainment, events: filteredEvents };
  })
  .filter(({ events }) => !!events && events.length > 0);

const getArrivalShorexPutData = (selectedArrivalSlotId, numOfUnits, isShorexEnhancementEnabled, bookedArrivalSlotId) => {
  const putData = [];
  for (let i = 0; i < numOfUnits; i += 1) {
    const shorexes = [{ shorexId: `${selectedArrivalSlotId}`, guestMask: '1' }];
    putData.push({
      shorexes: isShorexEnhancementEnabled && bookedArrivalSlotId > 0 ? [...shorexes, { shorexId: `${bookedArrivalSlotId}`, guestMask: '0' }] : shorexes,
      unitSeqNo: i,
    });
  }
  return putData;
};

export const filterSlotsByBackstop = ({ singleDate, duration }, backstopDate) => {
  const finishes = moment(singleDate).add(duration, 'minutes').format();
  return moment(finishes).isBefore(backstopDate);
};

export const getBookedShorexItems = (allShorex = {}) => allShorex.Units.map(({ UnitSeqNo, Shorexes = [] }) => ({
  UnitSeqNo,
  Shorexes: Shorexes.filter(({ SelectedGuestMask: selectedGuestMask }) => selectedGuestMask !== ''),
}));

const getBookedShorexItemsWithoutArrivals = bookedShorexItems => bookedShorexItems
  .map(({ UnitSeqNo, Shorexes }) => ({
    UnitSeqNo,
    Shorexes: Shorexes
      .filter(({ ShorexItem = {} }) => {
        const { code: shorexType } = ShorexItem;
        return shorexType !== 'ARRIVAL TIMES';
      })
      .map(({ ShorexItem = {}, SelectedGuestMask = '' }) => {
        const { id } = ShorexItem;
        return {
          shorexId: `${id}`,
          guestMask: SelectedGuestMask.replace(/;/g, ' '),
        };
      }),
  }));

const mergeArrivalAndNonArrivalShorexItems = (bookedShorexItems, { shorexes, unitSeqNo }) => {
  const bookedItem = bookedShorexItems.find(item => item.UnitSeqNo === unitSeqNo);

  if (!bookedItem) return { shorexes, unitSeqNo };

  return {
    shorexes: [...shorexes, ...bookedItem.Shorexes],
    unitSeqNo,
  };
};

export const getShorexPutData = (selectedArrivalSlotId, numOfUnits, bookedShorexItems, isShorexEnhancementEnabled = false, bookedArrivalSlotId) => {
  const arrivalShorexPutData = getArrivalShorexPutData(selectedArrivalSlotId, numOfUnits, isShorexEnhancementEnabled, bookedArrivalSlotId);
  if (isShorexEnhancementEnabled) {
    return {
      units: arrivalShorexPutData,
    };
  }
  const bookedShorexItemsWithoutArrivals = getBookedShorexItemsWithoutArrivals(bookedShorexItems);

  return {
    units: arrivalShorexPutData
      .map(item => mergeArrivalAndNonArrivalShorexItems(bookedShorexItemsWithoutArrivals, item)),
  };
};

export const getRemoveShorexData = (shorexPutData, bookedArrivalSlotId, isShorexEnhancementEnabled = false) => ({
  units: shorexPutData.units.map(({ shorexes = [], unitSeqNo }) => ({
    unitSeqNo,
    shorexes: !isShorexEnhancementEnabled
      ? shorexes.filter(({ shorexId }) => parseInt(shorexId, 10) !== parseInt(bookedArrivalSlotId, 10))
      : [
        {
          shorexId: `${bookedArrivalSlotId}`,
          guestMask: '0',
        },
      ],
  })),
});

export const getSelectedArrivalSlotIsNotAvailableMessage = () => 'Sorry, this arrival time is not available anymore';

export const SEASON_FAMILY = 'FAMILY';
export const SEASON_FAMILY_EARLY = 'FAMILY EARLY';
export const SEASON_BBW = 'BBW';
export const SEASON_BBW_EARLY = 'BBW EARLY';

export const getSeasonType = (seasonCode = '') => {
  if (!seasonCode || seasonCode === '') return SEASON_FAMILY;
  if (seasonCode === 'ADULT ONLY') return SEASON_BBW;
  return SEASON_FAMILY;
};

export const getSeasonCodeSuffix = (seasonType = '') => (seasonType === SEASON_BBW ? '_BBW' : '');

export const getExtendedSeasonType = (seasonType = SEASON_FAMILY, isEarlyAccess = false) => {
  if (seasonType === SEASON_BBW && isEarlyAccess) return SEASON_BBW_EARLY;
  if (seasonType === SEASON_BBW && !isEarlyAccess) return SEASON_BBW;
  if (seasonType === SEASON_FAMILY && isEarlyAccess) return SEASON_FAMILY_EARLY;
  return SEASON_FAMILY;
};

export const getEarliestDateToSignIn = (config, seasonSuffix = '', extendedSeasonType = SEASON_FAMILY, today = moment()) => {
  const {
    [`REACT_APP_NUMBER_OF_DAYS_BEFORE_BREAK${seasonSuffix}`]: numberOfDaysBeforeLogin,
    [`REACT_APP_LATEST_ARRIVAL_DATE${seasonSuffix}`]: latestArrivalDate,
    REACT_APP_NUMBER_OF_DAYS_BEFORE_MAIN_OPENING,
  } = config;
  const momentNumberOfDaysBeforeLogin = today.add(numberOfDaysBeforeLogin, 'd');
  const momentLatestArrivalDate = moment(latestArrivalDate);
  const earliestDate = momentLatestArrivalDate.isBefore(momentNumberOfDaysBeforeLogin) ? momentLatestArrivalDate : momentNumberOfDaysBeforeLogin;
  if (extendedSeasonType === SEASON_FAMILY || extendedSeasonType === SEASON_BBW) return earliestDate;
  return earliestDate.add(REACT_APP_NUMBER_OF_DAYS_BEFORE_MAIN_OPENING, 'days');
};
